'use client';

import { useTableFilter } from '@/hooks/use-table-filter';
import type { QueryParams } from '@/requests/types';
import type { ModelName } from '@pigello/pigello-matrix';
import { cloneDeep } from 'es-toolkit/compat';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import type { Filter, FilterEnumChoice, CustomFilter } from './constants';
import EnumFilter from './enum-filter';
import {
  getFilterIsActive,
  handleAddFilter,
  handleRemoveFilter,
} from './utils';
import Link from 'next/link';
import { Badge } from '../ui/badge';
import { Icons } from '../icons';

export default function TableEnumFilters({
  filters,
  isClient,
  tableId,
  modelName,
  overrideUrl,
  queryParamFilters,
  isFetchingTableData,
  enableGlobalFilters,
  customFilters,
}: {
  filters: Filter[];
  isClient: boolean;
  tableId: string;
  modelName: ModelName;
  overrideUrl?: string;
  queryParamFilters: QueryParams['filters'];
  isFetchingTableData: boolean;
  enableGlobalFilters?: boolean;
  customFilters?: CustomFilter[];
}) {
  const { filter, setTableFilter } = useTableFilter({ isClient, tableId });

  const toggleFilter = (
    filterVariant: Filter,
    choice: FilterEnumChoice,
    remove: boolean
  ) => {
    const filterCopy = cloneDeep(filter ?? {});

    if (remove) {
      handleRemoveFilter({ filterCopy, filterVariant, choice });
    } else {
      handleAddFilter({ filterCopy, filterVariant, choice });
    }

    setTableFilter(filterCopy);
  };

  const filteredFilters = filters.filter(
    (f) =>
      (f.enumChoices && f.enumChoices.length > 0) ||
      ((customFilters?.length ?? 0) > 0 &&
        customFilters?.some((customFilter) => customFilter.filterId === f.id))
  );

  if (filteredFilters.length === 0) return null;

  const hasAnyInActiveFilters = filteredFilters.some((filterVariant) => {
    return (filterVariant.enumChoices ?? []).some((choice) => {
      return !getFilterIsActive(filterVariant, choice, filter);
    });
  });

  const hasAnyCustomFilters = filteredFilters.some((filterVariant) =>
    customFilters?.some(
      (customFilter) => customFilter.filterId === filterVariant.id
    )
  );

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {(hasAnyInActiveFilters || hasAnyCustomFilters) && (
        <Popover modal>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              className='gap-1 rounded-full text-xs text-[#001E56]'
              style={{
                background:
                  'linear-gradient(to right, #D8D9FA 0%, #E0EFFF 51%, #D8D9FA 100%)',
                backgroundSize: '200% auto',
                backgroundPosition: 'left center',
                transition: '0.5s',
                borderColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundPosition = 'right center';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundPosition = 'left center';
              }}
            >
              Snabbfilter <Icons.ai className='size-3' />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className='flex max-h-[400px] w-80 flex-col gap-2 overflow-y-auto p-1'
            align='start'
            sideOffset={8}
          >
            {filteredFilters.map((filterVariant, index) => {
              return (
                <div
                  className={`flex flex-col ${index > 0 ? 'border-t border-border p-1' : ''}`}
                  key={filterVariant.title}
                >
                  <span className='px-2 pt-1 text-xs font-medium text-muted-foreground'>
                    {filterVariant.title}
                  </span>

                  {customFilters?.map((customFilterItem) => {
                    const {
                      filterId,
                      title,
                      href,
                      variant,
                      count,
                      isLoadingCount,
                    } = customFilterItem;
                    if (filterId === filterVariant.id) {
                      return (
                        <Link
                          key={title}
                          className='cursor-pointer p-2 last:mb-1 hover:bg-accent'
                          prefetch={false}
                          href={href}
                        >
                          <Badge
                            className='rounded-full text-xs'
                            variant={variant}
                          >
                            {title}{' '}
                            {isLoadingCount ? (
                              <Icons.loader className='ml-1 size-3.5 animate-spin' />
                            ) : (
                              (count ?? 0)
                            )}
                          </Badge>
                        </Link>
                      );
                    }
                  })}

                  {filterVariant.enumChoices?.map((choice) => {
                    const isActive = getFilterIsActive(
                      filterVariant,
                      choice,
                      filter
                    );

                    if (isActive) return null;

                    return (
                      <div
                        onClick={() => {
                          toggleFilter(filterVariant, choice, false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            toggleFilter(filterVariant, choice, false);
                          }
                        }}
                        role='button'
                        tabIndex={0}
                        className='p-2 hover:bg-accent'
                        key={choice.title}
                      >
                        <EnumFilter
                          isFetchingTableData={isFetchingTableData}
                          overrideUrl={overrideUrl}
                          modelName={modelName}
                          key={choice.title}
                          choice={choice}
                          filter={filter}
                          filterVariant={filterVariant}
                          toggleFilter={() => {}}
                          queryParamFilters={queryParamFilters}
                          enableGlobalFilters={enableGlobalFilters}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
